/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import { toLayoutValue } from '@coursera-web/cds-labs';

import { mapSearchHitsToCollections } from 'bundles/search-common/components/experiments/browseInSerp/BrowseInSerpUtils';
import MerchandisingInSearchCollectionPlaceholder from 'bundles/search-common/components/experiments/merchInSearch/MerchandisingInSearchCollectionPlaceholder';
import SearchCollectionsModule from 'bundles/search-common/components/experiments/merchInSearch/SearchCollectionsModule';
import { getMerchandisingInSearchCollectionsData } from 'bundles/search-common/components/experiments/merchInSearch/merchandisingInSearchUtils';
import type { SearchHit, SearchUpsellHit } from 'bundles/search-common/providers/searchTypes';
import { isSearchProductHit } from 'bundles/search-common/utils/utils';

const styles = {
  container: css`
    margin: var(--cds-spacing-400) 0;
    border-radius: var(--cds-border-radius-200);
    overflow: hidden;
  `,
};

const SearchPageMerchandisingCollection = ({
  hits,
  isLoading,
  forceOpenInNewTab,
  eventingData,
}: {
  hits: Array<SearchHit | SearchUpsellHit>;
  isLoading: boolean;
  forceOpenInNewTab?: boolean;
  eventingData?: Record<string, any>;
}) => {
  const [currentCollectionSetIndex, setCurrentCollectionSetIndex] = React.useState(0);
  const handleFilterChange = (index: number) => {
    setCurrentCollectionSetIndex(index);
  };
  const searchHits = useMemo(() => hits.filter(isSearchProductHit), [hits]);
  const {
    headline,
    description,
    isCollectionsLoading,
    collectionSets,
    layout,
    ctaText,
    ctaTarget,
    showCtaArrowIcon,
    backgroundImage,
    numOfResults,
  } = getMerchandisingInSearchCollectionsData(searchHits, isLoading) || {};
  if (!collectionSets || (collectionSets?.length === 0 && !isCollectionsLoading) || !numOfResults) return null;
  const searchCollections = mapSearchHitsToCollections(collectionSets);
  // remove collection title
  if (isCollectionsLoading && numOfResults) {
    return <MerchandisingInSearchCollectionPlaceholder numOfResults={numOfResults} />;
  }

  return (
    <div css={styles.container}>
      <SearchCollectionsModule
        productCardTrackingName="search_card"
        onFilterChange={handleFilterChange}
        currentCollectionSetIndex={currentCollectionSetIndex}
        // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        collections={[searchCollections[0]]}
        numOfEntriesPerCollection={numOfResults}
        isCollectionsLoading={isCollectionsLoading}
        showAllEntries
        lazyLoadImage
        description={description}
        headline={headline}
        layout={toLayoutValue(layout)}
        backgroundColor="light-blue"
        showRoundedCorners
        backgroundImage={backgroundImage}
        forceOpenInNewTab={forceOpenInNewTab}
        cta={{
          target: ctaTarget,
          text: ctaText,
          variant: 'primary',
          showCtaArrowIcon,
        }}
        eventingData={eventingData}
      />
    </div>
  );
};

export default SearchPageMerchandisingCollection;
